import React from "react";
import { useAppSelector, useAppDispatch } from "./hooks/reduxHooks";
import {
  selectSelectedDealId,
  selectDealDetail,
  selectDealDetailLoading,
  selectDealDetailError,
  fetchDealDetail,
} from "./DealsTableSlice_Store";
import type { DealDetail } from "./types/redux";

// ── Helpers ────────────────────────────────────────────────────

function formatArray(value: string[] | null | undefined): string {
  if (!value || value.length === 0) return "--";
  return value.join(", ");
}

function parseNameRole(entry: string): { name: string; role: string } {
  const m = entry.match(/^(.+?)\s+as\s+(.+)$/i);
  return m ? { name: m[1].trim(), role: m[2].trim() } : { name: entry, role: "" };
}

function PeopleTable({ items }: { items: string[] | null | undefined }): JSX.Element {
  if (!items || items.length === 0) return <span className="text-gray-500">--</span>;

  const parsed = items.map(parseNameRole);
  const hasRoles = parsed.some((p) => p.role);

  if (!hasRoles) return <span>{items.join(", ")}</span>;

  return (
    <table className="min-w-full text-sm">
      <thead>
        <tr className="border-b border-gray-200">
          <th className="text-left py-1 pr-4 font-medium text-gray-600">Name</th>
          <th className="text-left py-1 font-medium text-gray-600">Role</th>
        </tr>
      </thead>
      <tbody>
        {parsed.map((item, idx) => (
          <tr key={idx} className="border-b border-gray-100 last:border-0">
            <td className="py-1 pr-4">{item.name}</td>
            <td className="py-1 text-gray-600">{item.role || "--"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Single labelled field */
function Field({ label, children }: { label: string; children: React.ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-xs desktop:text-sm font-medium text-gray-500 uppercase tracking-wider">{label}</dt>
      <dd className="mt-1 text-sm desktop:text-base text-gray-900 break-words">{children}</dd>
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────

export default function DealDetailsPanel(): JSX.Element | null {
  const dispatch = useAppDispatch();
  const dealId = useAppSelector(selectSelectedDealId);
  const deal = useAppSelector((state) => selectDealDetail(state, dealId));
  const loadingId = useAppSelector(selectDealDetailLoading);
  const error = useAppSelector(selectDealDetailError);

  const isLoading = loadingId === dealId;

  if (!dealId) return null;

  // ── Loading state ──
  if (isLoading && !deal) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-600">Loading deal details...</span>
      </div>
    );
  }

  // ── Error state ──
  if (error && !deal) {
    return (
      <div className="text-center py-12 px-4">
        <div className="text-red-600 mb-2">Failed to load deal details</div>
        <div className="text-sm text-gray-500 mb-3">{error}</div>
        <button
          onClick={() => dispatch(fetchDealDetail(dealId))}
          className="px-4 py-2 bg-cvc-blue text-white rounded hover:bg-cvc-blue/90 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!deal) return null;

  const isSensitive = (deal.sensitivity as string | undefined)?.toLowerCase() === "true";
  const normalizedStage = (deal.dealStage || deal.stage || "").toLowerCase();
  const isDead = normalizedStage === "dead";
  const isExited = normalizedStage === "exited";

  return (
    <div className="flex flex-col h-full">
      {/* Header — close affordance lives in the panel tab bar (DealsLayout) */}
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        <h2 className="text-base desktop:text-lg font-bold text-gray-800 truncate">
          {deal.dealName || "Deal Details"}
        </h2>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        {/* Company name */}
        <Field label="Company Name">
          <span className="text-base desktop:text-lg font-semibold">
            {isSensitive ? "Sensitive" : deal.portfolioCompanyName || "--"}
          </span>
        </Field>

        {/* Description */}
        <Field label="Description">
          <span className="text-sm desktop:text-base text-gray-900">{deal.business || deal.description || "No description"}</span>
        </Field>

        {/* Key info grid */}
        <dl className="grid grid-cols-1 desktop:grid-cols-2 gap-x-6 gap-y-3">
          <Field label="Sector">{deal.sector || "--"}</Field>
          <Field label="Investment Year">{deal.investmentYear || deal.year || "--"}</Field>
          <Field label="Country">{deal.country || "--"}</Field>
          <Field label="Stage">{deal.dealStage || deal.stage || "--"}</Field>

          {deal.fund && <Field label="Fund">{deal.fund}</Field>}
          {deal.closingDate && <Field label="Close Year">{String(deal.closingDate).substring(0, 4)}</Field>}
          {deal.exitYear && <Field label="Exit Year">{deal.exitYear}</Field>}
          {deal.lastIcStage && <Field label="Last IC Stage">{deal.lastIcStage}</Field>}

          {(deal.efrontIoContact1 || deal.efrontIoContact2) && (
            <Field label="Deal Contacts">
              <PeopleTable items={[deal.efrontIoContact1, deal.efrontIoContact2].filter(Boolean) as string[]} />
            </Field>
          )}

          <Field label="Team">{formatArray(deal.team || deal.teamMembers)}</Field>

          {deal.deadReason && <Field label="Dead Reason">{deal.deadReason}</Field>}
          {isDead && deal.lastActiveStatus && <Field label="Last Active Stage">{deal.lastActiveStatus}</Field>}
          {isExited && deal.exitCompleted && <Field label="Exit Completed">{deal.exitCompleted}</Field>}
        </dl>

        {/* Divider */}
        <div className="border-t border-gray-200" />

        {/* Bottom section */}
        <dl className="space-y-4">
          <Field label="Business Overview">
            <span className="text-sm desktop:text-base text-gray-700 whitespace-pre-wrap leading-relaxed">
              {deal.companyOverview || deal.company_overview || deal.COMPANY_OVERVIEW || "--"}
            </span>
          </Field>
          <Field label="Vendor">{formatArray(deal.vendors || deal.vendor)}</Field>
          <div>
            <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">People</dt>
            <dd className="mt-1 text-base text-gray-900">
              <PeopleTable items={deal.people} />
            </dd>
          </div>
          <div>
            <dt className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-2">Advisors</dt>
            <dd className="mt-1 text-base text-gray-900">
              <PeopleTable items={deal.advisors} />
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
}
